import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { SensitiveSellerIdModal } from '@/components/SensitiveSellerIdModal/SensitiveSellerIdModal'
import { useInstitution } from '@/contexts/InstitutionContext'
import { useAuth } from '@/hooks/useAuth'
import { ApiClientError } from '@/lib/api/client'
import { getMe } from '@/lib/api/auth.service'
import { updateInstitutionPaymentGateway } from '@/lib/api/payment-gateway.service'
import { notifyInstitutionPaymentGatewayUpdated } from '@/lib/institution-payment-gateway'
import type { InstitutionMe } from '@/types/api'
import styles from './GatewayPagamentoPage.module.css'

const RETURN_SORTEE_URL = 'https://sortee.io/gateway-pagamento'
const DOEY_REGISTER_BASE_URL = 'https://doey.com.br/register-doey-sortee'
const DOEY_LOGIN_URL = 'https://doey.com.br/login'

type DoeyRegisterQueryParams = {
  name: string
  email: string
  document: string
}

function resolveInstitutionDocument(source: {
  person_type?: 'pf' | 'pj'
  document_cpf?: string | null
  document_cnpj?: string | null
}): string {
  if (source.person_type === 'pj') {
    return source.document_cnpj?.trim() ?? ''
  }
  if (source.person_type === 'pf') {
    return source.document_cpf?.trim() ?? ''
  }

  return source.document_cnpj?.trim() || source.document_cpf?.trim() || ''
}

function toDoeyRegisterQueryParams(source: InstitutionMe): DoeyRegisterQueryParams {
  return {
    name: source.name.trim(),
    email: source.email.trim(),
    document: resolveInstitutionDocument(source),
  }
}

function buildDoeyRegisterUrl(params: DoeyRegisterQueryParams): string {
  const searchParams = new URLSearchParams({
    'return-sortee': RETURN_SORTEE_URL,
  })

  if (params.name) searchParams.set('name', params.name)
  if (params.email) searchParams.set('email', params.email)
  if (params.document) searchParams.set('document', params.document)

  return `${DOEY_REGISTER_BASE_URL}?${searchParams.toString()}`
}

function extractApiErrorMessage(error: ApiClientError): string {
  if (error.errors) {
    const firstEntry = Object.values(error.errors).find(
      (messages) => Array.isArray(messages) && messages.length > 0
    )
    if (firstEntry?.[0]) {
      return firstEntry[0]
    }
  }

  return error.message
}

function resolveSellerId(
  storedId: string | null | undefined,
  useridFromUrl: string
): string {
  if (useridFromUrl) return useridFromUrl
  return storedId ?? ''
}

export function GatewayContaTab() {
  const { guard, isCollaborator, refreshMe } = useAuth()
  const { institutionId: selectedInstitutionId } = useInstitution()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const useridFromUrl = searchParams.get('userid')?.trim() ?? ''
  const isAdminWithoutInstitution = guard === 'admin' && selectedInstitutionId === null
  const canManage =
    (guard === 'institution' || guard === 'admin') && !isCollaborator && !isAdminWithoutInstitution

  const [institutionMe, setInstitutionMe] = useState<InstitutionMe | null>(null)
  const [sellerId, setSellerId] = useState(() => useridFromUrl)
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null)
  const [isConfirmingSellerId, setIsConfirmingSellerId] = useState(false)

  const doeyRegisterUrl = useMemo(() => {
    if (!institutionMe) {
      return buildDoeyRegisterUrl({ name: '', email: '', document: '' })
    }

    return buildDoeyRegisterUrl(toDoeyRegisterQueryParams(institutionMe))
  }, [institutionMe])

  const hasSellerId = Boolean(
    (sellerId || institutionMe?.default_doey_seller_id || '').trim()
  )

  const doeyAccountAction = useMemo(
    () =>
      hasSellerId
        ? { href: DOEY_LOGIN_URL, label: 'Entrar na Doey', icon: 'mdi:login' as const }
        : {
            href: doeyRegisterUrl,
            label: 'Abrir cadastro na Doey',
            icon: 'mdi:open-in-new' as const,
          },
    [doeyRegisterUrl, hasSellerId]
  )

  useEffect(() => {
    if (!canManage) {
      setInstitutionMe(null)
      setIsLoading(false)
      return
    }

    let mounted = true

    const load = async () => {
      setIsLoading(true)
      setSubmitError(null)
      try {
        const me = await getMe()
        if (!mounted) return
        setInstitutionMe(me)
        setSellerId(resolveSellerId(me.default_doey_seller_id, useridFromUrl))
      } catch {
        if (!mounted) return
        setInstitutionMe(null)
        setSubmitError('Não foi possível carregar as configurações do gateway.')
      } finally {
        if (mounted) setIsLoading(false)
      }
    }

    void load()

    return () => {
      mounted = false
    }
  }, [canManage, useridFromUrl])

  const saveDefaultSellerId = useCallback(
    async (sellerIdToSave: string, code: string) => {
      setSubmitError(null)
      setSubmitSuccess(null)
      setIsSubmitting(true)

      try {
        const updated = await updateInstitutionPaymentGateway({
          default_doey_seller_id: sellerIdToSave,
          seller_id_totp_code: code,
        })
        setSellerId(updated.default_doey_seller_id ?? '')
        notifyInstitutionPaymentGatewayUpdated()
        try {
          await refreshMe()
        } catch {
          // O ID foi salvo; uma falha ao atualizar o contexto local não desfaz a alteração.
        }
        setSubmitSuccess('ID padrão salvo com sucesso.')
        return true
      } catch (error) {
        if (error instanceof ApiClientError) {
          setSubmitError(extractApiErrorMessage(error))
        } else {
          setSubmitError('Não foi possível salvar o ID padrão.')
        }
        return false
      } finally {
        setIsSubmitting(false)
      }
    },
    [refreshMe]
  )

  const handleSave = useCallback(() => {
    setSubmitError(null)
    setSubmitSuccess(null)
    setIsConfirmingSellerId(true)
  }, [])

  if (!canManage) {
    return (
      <div className={styles.card}>
        <p className={styles.forbiddenMessage} role="alert">
          {isAdminWithoutInstitution
            ? 'Selecione uma instituição no painel para configurar o gateway de pagamento.'
            : 'Apenas o titular da conta da instituição pode configurar o gateway de pagamento.'}
        </p>
      </div>
    )
  }

  return (
    <div className={styles.card}>
      <section className={styles.section} aria-labelledby="gateway-conta-doey-title">
        <h2 id="gateway-conta-doey-title" className={styles.sectionTitle}>
          <Icon className={styles.sectionTitleIcon} icon="mdi:account-cash-outline" aria-hidden />
          Conta na Doey
        </h2>

        <div className={styles.instructions}>
          <p>
            Para receber pagamentos via PIX nas campanhas, sua instituição precisa de uma conta na
            Doey. Crie ou acesse sua conta no site da Doey e copie o ID do vendedor exibido no painel
            deles.
          </p>
          <p>
            O ID informado abaixo será o padrão da sua instituição. Você ainda pode definir um ID
            específico em cada campanha, na aba Valores da edição da campanha.
          </p>
        </div>

        <a
          className={styles.registerButton}
          href={doeyAccountAction.href}
          target="_blank"
          rel="noopener noreferrer"
        >
          <Icon icon={doeyAccountAction.icon} aria-hidden />
          {doeyAccountAction.label}
        </a>
      </section>

      <section className={styles.section} aria-labelledby="gateway-conta-seller-id-title">
        <h2 id="gateway-conta-seller-id-title" className={styles.sectionTitle}>
          <Icon className={styles.sectionTitleIcon} icon="mdi:identifier" aria-hidden />
          ID padrão do vendedor
        </h2>

        {isLoading ? (
          <p className={styles.loadingText}>Carregando...</p>
        ) : (
          <>
            <div className={styles.field}>
              <label className={styles.label} htmlFor="institution-default-doey-seller-id">
                ID do vendedor (Doey)
                <span className={styles.labelHint}>
                  Identificador do vendedor na plataforma Doey para processamento de pagamentos
                </span>
              </label>
              <input
                id="institution-default-doey-seller-id"
                type="text"
                className={styles.input}
                maxLength={36}
                placeholder="Ex: 123e4567-e89b-12d3-a456-426614174000"
                value={sellerId}
                onChange={(event) => setSellerId(event.target.value)}
                disabled={isSubmitting}
              />
              {useridFromUrl && (
                <span className={styles.labelHint}>
                  ID recebido da Doey. Confirme com o autenticador para salvar.
                </span>
              )}
            </div>

            {submitError && (
              <p className={styles.errorMessage} role="alert">
                {submitError}
              </p>
            )}
            {submitSuccess && (
              <p className={styles.successMessage} role="status">
                {submitSuccess}
              </p>
            )}

            <button
              type="button"
              className={styles.saveButton}
              onClick={handleSave}
              disabled={isSubmitting || isLoading}
            >
              {isSubmitting ? 'Salvando...' : 'Salvar ID padrão'}
            </button>
          </>
        )}
      </section>
      {isConfirmingSellerId && (
        <SensitiveSellerIdModal
          nextId={sellerId}
          isSubmitting={isSubmitting}
          errorMessage={submitError}
          onCancel={() => setIsConfirmingSellerId(false)}
          onSubmit={(code) => {
            void saveDefaultSellerId(sellerId, code).then((saved) => {
              if (saved) {
                setIsConfirmingSellerId(false)
                if (useridFromUrl) navigate('/gateway-pagamento', { replace: true })
              }
            })
          }}
        />
      )}
    </div>
  )
}
